import {
  FloatingFormItem,
  Group,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import VehicleSelectField from '@/components/_common/VehicleSelectField';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { CustomerVehicleReduced } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { ApoliceLogo } from '@/core/domain/porto-seguro/apolices/apolices.types';
import { usePortoPoliciesList } from '@/core/domain/porto/policies/porto-policies.hooks';
import {
  ACTIVITY_DETAIL_KEY,
  ACTIVITY_HISTORY_KEY,
  ACTIVITY_LIST_KEY,
  useActivityDetail,
} from '@/core/domain/visual-management/activities/activities.hooks';
import { PENDING_ACTIVITY_LIST_KEY } from '@/core/domain/visual-management/pending-activities/pending-activities.hooks';
import {
  useCreateWorkOrder,
  useWorkOrderCreateByPorto,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import {
  CreateWorkOrderRequest,
  CustomerType,
  WorkOrderCreateByPolicyCompanyCode,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import { isAppPortocar } from '@/index';
import { applyCpfCnpjMask } from '@/utils/mask';
import { DateTimeFormat } from '@/utils/moment';
import {
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Skeleton,
  Typography,
} from 'antd';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  activityId?: string;
  onFinish: VoidFunction;
}

interface FormValues {
  customerId: number;
  vehicleId: number;
  activityId: string;
  licensePlate: string;
  prisma?: string;
  currentKilometers?: number;
  tankLevel?: WorkOrderComplementTankLevel;
  policy: {
    companyCode?: string;
    policyNumber?: string;
    customer?: {
      birthDate?: string;
      documentNumber?: string;
      contacts?: { email: string; phone: string };
      name?: string;
    };
    vehicle?: {
      chassi?: string;
      model?: string;
      brand?: string;
      yearFabrication?: number;
      yearModel?: number;
    };
  };
  lastOilChange?: Moment;
  lastOilChangeKilometers?: number;
}

export default function GenerateWorkOrderWithPolicy(props: Props) {
  const [form] = Form.useForm<FormValues>();
  const intl = useIntl();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [vehicleSelected, setVehicleSelected] =
    useState<CustomerVehicleReduced>();

  const hasFuelEletric = useMemo(() => {
    return vehicleSelected?.fuel?.name?.toLowerCase()?.includes('elétrico');
  }, [vehicleSelected]);
  const policyNumber = Form.useWatch(['policy', 'policyNumber'], form);
  const licensePlate = Form.useWatch(['licensePlate'], form);
  const vehicleId = Form.useWatch(['vehicleId'], form);

  const createWorkOrder = useCreateWorkOrder();
  const workOrderCreateByPolicy = useWorkOrderCreateByPorto();
  const activityDetail = useActivityDetail(props.activityId, props.opened);
  const activity = activityDetail.data;

  const portoPoliciesList = usePortoPoliciesList(
    licensePlate,
    isAppPortocar && !!licensePlate,
  );

  const queryChanel = useQueryChanel();

  const { data: configBudget, mutateAsync: configBudgetMutate } =
    useWorkorderConfigurationCompany();

  useEffect(() => {
    if (activity) {
      form.setFieldsValue({
        activityId: props.activityId,
        customerId: activity.customerId,
        vehicleId: activity.vehicleId,
        licensePlate: activity.licensePlate,
      });
      configBudgetMutate(activity.companyId);
    }
  }, [activityDetail.dataUpdatedAt]);

  useEffect(() => {
    if (portoPoliciesList.data?.length) {
      const policy = portoPoliciesList.data[0];
      const { email, phone, ...restCustomer } = policy.customer;

      form.setFieldsValue({
        policy: {
          companyCode: policy.companyCode,
          policyNumber: policy.policyNumber,
          customer: { ...restCustomer, contacts: { email, phone } },
          vehicle: policy.vehicle,
        },
      });
    }
  }, [portoPoliciesList.dataUpdatedAt]);

  const handleSubmit = async () => {
    try {
      if (!activity) return;

      const { policy, ...values } = await form.validateFields();

      if (!policy.policyNumber) {
        const obj: CreateWorkOrderRequest = {
          ...values,
          activityId: activity.id,
          companyId: activity.companyId,
          customerType: CustomerType.Private,
          estimatorId: activity.estimator.id,
          workOrderType: WorkOrderComplementType.WorkOrder,
          customerId: values.vehicleId ? undefined : values.customerId,
          vehicleId: values.vehicleId || undefined,
          entryDate: activity.entryDate,
          estimatedLeaveDate: activity.estimatedLeaveDate,
          lastOilChange: values.lastOilChange
            ? values.lastOilChange.format(DateTimeFormat)
            : undefined,
        };
        await createWorkOrder.mutateAsync(obj);
      } else {
        await workOrderCreateByPolicy.mutateAsync({
          activityId: activity.id,
          companyId: activity.companyId,
          customerType: CustomerType.Private,
          estimatorId: activity.estimator.id,
          workOrderType: WorkOrderComplementType.WorkOrder,
          vehicleId: values.vehicleId || undefined,
          customerId: values.customerId || undefined,
          companyCode:
            `domain.enums.company_code.${policy.companyCode?.toLowerCase()}` as WorkOrderCreateByPolicyCompanyCode,
          entryDate: activity.entryDate,
          policyNumber: policy.policyNumber as string,
          customer: policy.customer,
          vehicle: {
            ...policy.vehicle,
            licensePlate: values.licensePlate,
            prisma: values.prisma,
            currentKilometers: values.currentKilometers,
            tankLevel: values.tankLevel,
          },
          lastOilChange: values.lastOilChange
            ? values.lastOilChange.format(DateTimeFormat)
            : undefined,
        });
      }

      queryChanel({
        invalidateQueries: [
          [ACTIVITY_DETAIL_KEY, { activityId: activity.id }],
          [ACTIVITY_HISTORY_KEY],
          [ACTIVITY_LIST_KEY],
          [PENDING_ACTIVITY_LIST_KEY],
        ],
      });

      props.onFinish();
      props.onClose();
    } catch {}
  };

  return (
    <Modal
      width={600}
      open={props.opened}
      onCancel={props.onClose}
      title="Criação de orçamento"
      style={{ position: 'relative' }}
      okButtonProps={{
        disabled: portoPoliciesList.isLoading || activityDetail.isLoading,
      }}
      okText="Gerar orçamento"
      onOk={handleSubmit}
      confirmLoading={
        workOrderCreateByPolicy.isLoading || createWorkOrder.isLoading
      }
    >
      {activityDetail.isLoading && (
        <ModalLoadingOverlay messages={['Carregando...']} posAbsolute />
      )}
      <Form form={form}>
        <Form.Item
          hidden
          name={['policy', 'companyCode']}
          rules={
            !!portoPoliciesList.data?.length
              ? [{ required: true, message: 'Selecione uma apólice' }]
              : undefined
          }
        />
        <Form.Item hidden name={['policy', 'policyNumber']} />
        <Form.Item hidden name={['policy', 'customer']} />
        <Form.Item hidden name={['policy', 'vehicle']} />
        <Form.Item hidden name="activityId" />
        <Form.Item hidden name="licensePlate" />

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem name="customerId" label="Cliente">
              <CustomersSelectField disabled />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem name="vehicleId" label="Veículo">
              <VehicleSelectField
                customerId={activity?.customerId}
                disabled={!!activity?.vehicleId}
                onVehicleSelect={(vehicle) => {
                  form.setFieldValue('licensePlate', vehicle?.licensePlate);
                  setVehicleSelected(vehicle);
                }}
              />
            </FloatingFormItem>
          </Col>

          {(!!vehicleId || !!licensePlate) && (
            <>
              <Col span={24}>
                <Typography.Title level={4}>
                  {intl.formatMessage({ id: 'workOrders.domain.vehicle' })}
                </Typography.Title>
              </Col>

              <Col span={8}>
                <FloatingFormItem
                  name="prisma"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.prisma',
                  })}
                  rules={[
                    {
                      required: configBudget?.prisma,
                      message: intl.formatMessage({
                        id: 'workOrders.fieldRequiredByConfigCompany',
                      }),
                    },
                  ]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col span={8}>
                <FloatingFormItem
                  name="currentKilometers"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.currentKilometers',
                  })}
                  rules={[
                    {
                      required: isAppPortocar || configBudget?.isKmRequired,
                      message: intl.formatMessage({
                        id: 'workOrders.fieldRequiredByConfigCompany',
                      }),
                    },
                  ]}
                >
                  <InputDecimal fractions={0} min={0} />
                </FloatingFormItem>
              </Col>

              <Col span={8}>
                <FloatingFormItem
                  name="tankLevel"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.tankLevel',
                  })}
                  rules={[
                    {
                      required: configBudget?.tankLevel,
                      message: intl.formatMessage({
                        id: 'workOrders.fieldRequiredByConfigCompany',
                      }),
                    },
                  ]}
                >
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    options={Object.values(WorkOrderComplementTankLevel).map(
                      (a) => ({
                        label: intl.formatMessage({
                          id: `workOrders.${a}`,
                        }),
                        value: a,
                      }),
                    )}
                  />
                </FloatingFormItem>
              </Col>

              {isAppPortocar && (
                <>
                  <Col span={12}>
                    <FloatingFormItem
                      name="lastOilChange"
                      label={intl.formatMessage({
                        id: 'workOrders.domain.complements.lastOilChange',
                      })}
                      rules={hasFuelEletric ? [] : [defaultEmptyFieldError]}
                    >
                      <InputDate
                        disabledDate={(date) => date.isAfter(moment(), 'day')}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={12}>
                    <FloatingFormItem
                      name="lastOilChangeKilometers"
                      label={intl.formatMessage({
                        id: 'workOrders.domain.complements.lastOilChangeKilometers',
                      })}
                      rules={hasFuelEletric ? [] : [defaultEmptyFieldError]}
                    >
                      <InputDecimal fractions={0} min={0} />
                    </FloatingFormItem>
                  </Col>
                </>
              )}
            </>
          )}

          <Col span={24}>
            <Typography.Title level={3} style={{ margin: 0, marginBottom: 24 }}>
              Apólices • {licensePlate}
            </Typography.Title>

            <Form.Item shouldUpdate noStyle>
              {({ getFieldError }) => {
                const errors = getFieldError(['policy', 'companyCode']);
                return errors.length ? (
                  <span style={{ color: 'red' }}>{errors[0]}</span>
                ) : null;
              }}
            </Form.Item>

            <Skeleton loading={portoPoliciesList.isLoading}>
              <div className="workorder-polices">
                {!portoPoliciesList.data?.length && !licensePlate && (
                  <Card style={{ borderRadius: 8 }}>
                    <Empty description="Informe uma placa para listar as apolices" />
                  </Card>
                )}

                {!portoPoliciesList.data?.length && licensePlate?.length === 7 && (
                  <Card style={{ borderRadius: 8 }}>
                    <Empty description="Não foi encontrado apolices para a placa informada" />
                  </Card>
                )}

                {licensePlate?.length === 7 &&
                  portoPoliciesList.data?.map((policy) => (
                    <div
                      className="workorder-policy-card"
                      key={policy.policyNumber}
                      data-active={policy.policyNumber === policyNumber}
                      onClick={() => {
                        form.setFields([{ name: 'customerId', errors: [] }]);

                        if (policyNumber === policy.policyNumber) {
                          form.setFieldValue('policy', undefined);
                        } else {
                          const { email, phone, ...restCustomer } =
                            policy.customer;

                          form.setFieldsValue({
                            policy: {
                              companyCode: policy.companyCode,
                              policyNumber: policy.policyNumber,
                              customer: {
                                ...restCustomer,
                                contacts: { email, phone },
                              },
                              vehicle: policy.vehicle,
                            },
                          });
                        }
                      }}
                    >
                      <Group gap={16} align="start">
                        <Radio checked={policy.policyNumber === policyNumber} />
                        <div className="policy-content">
                          <Group>
                            <img src={ApoliceLogo[policy.companyCode]} />
                            <Divider type="vertical" />
                            <span>
                              Vigència:{' '}
                              {moment(policy.finalValidityDate).format(
                                'DD/MM/YYYY',
                              )}
                            </span>
                          </Group>

                          <div>
                            <strong>{`${licensePlate} • ${policy.vehicle.brand} ${policy.vehicle.model}`}</strong>
                            <div>{policy.customer.name}</div>
                            <div>
                              {applyCpfCnpjMask(policy.customer.documentNumber)}
                            </div>
                          </div>
                        </div>
                      </Group>
                    </div>
                  ))}
              </div>
            </Skeleton>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
